import React from 'react';
import { Box, Typography, useTheme, styled } from '@mui/material';
import { Keyframes } from '@emotion/react';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  progressCycle,
  questionCycle1,
  questionCycle2,
  questionCycle3,
  questionCycle4,
  surveyCardAppear,
} from '../../styles/animations';

type MockupPosition = 1 | 2 | 3 | 4;

interface SurveyMockupProps {
  /** Which mockup instance (1-4), determines position and animation offset */
  instance: MockupPosition;
}

// Progress bar with cycling animation
const AnimatedProgressBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'animationDelay' && prop !== 'animationDuration',
})<{ animationDelay: string; animationDuration: string }>(
  ({ animationDelay, animationDuration }) => ({
    height: 4,
    borderRadius: 2,
    background: 'linear-gradient(90deg, #FF9A76, #A04CFF)',
    animation: `${progressCycle} ${animationDuration} ease-in-out infinite`,
    animationDelay,
    // Respect reduced motion preference
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      width: '50%',
    },
  })
);

// Question container with visibility animation
const AnimatedQuestion = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'animationName' &&
    prop !== 'animationDelay' &&
    prop !== 'animationDuration' &&
    prop !== 'questionIndex',
})<{
  animationName: Keyframes;
  animationDelay: string;
  animationDuration: string;
  questionIndex: number;
}>(({ animationName, animationDelay, animationDuration, questionIndex }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  opacity: 0,
  animation: `${animationName} ${animationDuration} ease-in-out infinite`,
  animationDelay,
  // Respect reduced motion preference - show only first question statically
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: questionIndex === 0 ? 1 : 0,
  },
}));

/**
 * Animated survey mockup card for the hero section
 * Displays a fake survey interface with cycling questions
 * Matches the original static landing page exactly
 */
export const SurveyMockup: React.FC<SurveyMockupProps> = ({ instance }) => {
  const theme = useTheme();

  // Different durations and delays for each instance to avoid synchronization
  // Using prime-ish numbers so they rarely align
  const timings: Record<MockupPosition, { duration: string; delay: string }> = {
    1: { duration: '14s', delay: '0s' },
    2: { duration: '17s', delay: '-3s' },
    3: { duration: '19s', delay: '-7s' },
    4: { duration: '15s', delay: '-5s' },
  };

  // Position styles matching original CSS exactly
  const positions: Record<MockupPosition, React.CSSProperties> = {
    1: {
      top: '50%',
      right: '5%',
      transform: 'translateY(-50%)',
    },
    2: {
      top: '35%',
      left: '5%',
      transform: 'translateY(-50%)',
    },
    3: {
      top: '15%',
      right: '15%',
    },
    4: {
      bottom: '10%',
      left: '12%',
    },
  };

  const { duration: animationDuration, delay: animationDelay } = timings[instance];
  const positionStyle = positions[instance];

  return (
    <Box
      sx={{
        position: 'absolute',
        opacity: 0.4,
        pointerEvents: 'none',
        display: { xs: 'none', lg: 'block' },
        ...positionStyle,
      }}
    >
      <Box
        sx={{
          width: 280,
          height: 220,
          bgcolor: (theme) =>
            theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          p: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
          position: 'relative',
          overflow: 'hidden',
          animation: `${surveyCardAppear} 1s ease-out`,
          // Respect reduced motion preference
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      >
        {/* Progress Bar */}
        <Box
          sx={{
            width: '100%',
            height: 4,
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            mb: '20px',
            overflow: 'hidden',
          }}
        >
          <AnimatedProgressBar
            animationDelay={animationDelay}
            animationDuration={animationDuration}
          />
        </Box>

        {/* Questions Container */}
        <Box sx={{ position: 'relative', height: 160 }}>
          {/* Question 1: Radio Buttons */}
          <AnimatedQuestion
            animationName={questionCycle1}
            animationDelay={animationDelay}
            animationDuration={animationDuration}
            questionIndex={0}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: theme.palette.primary.main,
                mb: 0.5,
                fontSize: '0.7rem',
              }}
            >
              Course Feedback Survey
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.85rem' }}>
              How would you rate this course?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {['Excellent', 'Good', 'Fair', 'Poor'].map((option, idx) => (
                <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {idx === 1 ? (
                    <RadioButtonCheckedIcon
                      sx={{ fontSize: 16, color: theme.palette.primary.main }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{
                        fontSize: 16,
                        color:
                          theme.palette.mode === 'light'
                            ? 'rgba(0,0,0,0.3)'
                            : 'rgba(255,255,255,0.3)',
                      }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {option}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnimatedQuestion>

          {/* Question 2: Checkboxes */}
          <AnimatedQuestion
            animationName={questionCycle2}
            animationDelay={animationDelay}
            animationDuration={animationDuration}
            questionIndex={1}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: theme.palette.primary.main,
                mb: 0.5,
                fontSize: '0.7rem',
              }}
            >
              Course Feedback Survey
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.85rem' }}>
              What aspects did you enjoy?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {['Lectures', 'Materials', 'Discussions', 'Projects'].map((option, idx) => (
                <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {idx === 0 || idx === 2 ? (
                    <CheckBoxIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon
                      sx={{
                        fontSize: 16,
                        color:
                          theme.palette.mode === 'light'
                            ? 'rgba(0,0,0,0.3)'
                            : 'rgba(255,255,255,0.3)',
                      }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {option}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnimatedQuestion>

          {/* Question 3: Text Input */}
          <AnimatedQuestion
            animationName={questionCycle3}
            animationDelay={animationDelay}
            animationDuration={animationDuration}
            questionIndex={2}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: theme.palette.primary.main,
                mb: 0.5,
                fontSize: '0.7rem',
              }}
            >
              Course Feedback Survey
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.85rem' }}>
              Any additional comments?
            </Typography>
            <Box
              sx={{
                border: (theme) =>
                  theme.palette.mode === 'light'
                    ? '1px solid rgba(0,0,0,0.1)'
                    : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1,
                p: 1.5,
                minHeight: 60,
                bgcolor: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)',
                }}
              >
                Type your feedback here...
              </Typography>
            </Box>
          </AnimatedQuestion>

          {/* Question 4: Star Rating */}
          <AnimatedQuestion
            animationName={questionCycle4}
            animationDelay={animationDelay}
            animationDuration={animationDuration}
            questionIndex={3}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: theme.palette.primary.main,
                mb: 0.5,
                fontSize: '0.7rem',
              }}
            >
              Course Feedback Survey
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, fontSize: '0.85rem' }}>
              Overall satisfaction
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
              {[1, 2, 3, 4, 5].map((star) =>
                star <= 4 ? (
                  <StarIcon key={star} sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                ) : (
                  <StarBorderIcon
                    key={star}
                    sx={{ fontSize: 24, color: theme.palette.primary.main }}
                  />
                )
              )}
            </Box>
          </AnimatedQuestion>
        </Box>
      </Box>
    </Box>
  );
};
